import { useCallback, useState } from "react";
import type {
  SubmitErrorHandler,
  SubmitHandler,
  UseFormReturn,
} from "react-hook-form";
import { getAnalytics } from "@tm/insurance/analytics";
import { usePlanType } from "@tm/ui";
import { trackEvent } from "@tm/insurance/analytics/xdm";
import type { ITravellerDetailsFormV2 } from "../../AddTravellerDetail/type";
import { uploadTemplate } from "../../ExcelUpload/utils/uploadTemplate";
import type {
  TravellerDetailsV2CheckoutFormData,
  TravellerV2Component,
} from "../TravellerDetailsV2.types";

interface UseTravellerSubmitParams {
  methods: Pick<UseFormReturn<ITravellerDetailsFormV2>, "handleSubmit">;
  components: TravellerV2Component[];
  uploadedExcelFile: File | null;
  onBuyProp?: (formData: TravellerDetailsV2CheckoutFormData) => void | Promise<void>;
  getSelectedCountry?: (
    country: { countryCode?: string; countryFlag?: string } | undefined,
  ) => void;
  selectedCountry?: { countryCode?: string; countryFlag?: string };
  onSubmitError: SubmitErrorHandler<ITravellerDetailsFormV2>;
}

export function useTravellerSubmit({
  methods,
  components,
  uploadedExcelFile,
  onBuyProp,
  getSelectedCountry,
  selectedCountry,
  onSubmitError,
}: UseTravellerSubmitParams) {
  const { handleSubmit } = methods;
  const { type } = usePlanType();
  const analytics = getAnalytics();

  const [localSubmitting, setLocalSubmitting] = useState(false);

  const onSubmit: SubmitHandler<ITravellerDetailsFormV2> = useCallback(
    async (formData) => {
      setLocalSubmitting(true);
      try {
        if (getSelectedCountry && selectedCountry) {
          getSelectedCountry(selectedCountry);
        }
        analytics?.trackTravDetailsEvents(
          { actionType: "traveller_name_filled" },
          "pageView",
          type,
        );
        analytics?.trackTravDetailsEvents(
          { actionType: "contact_details_filled" },
          "pageView",
          type,
        );
        analytics?.trackTravDetailsEvents(
          { actionType: "dob_selected" },
          "pageView",
          type,
        );
        analytics?.trackTravDetailsEvents(
          { actionType: "gender_selected" },
          "pageView",
          type,
        );

        const excelSectionData = components.find(
          (c) => (c.data as any)?.excelSectionData,
        )?.data as
          | { excelSectionData?: { upload?: { isExcelBypass?: boolean } } }
          | undefined;
        const rawExcel = excelSectionData?.excelSectionData;
        const isExcelBypass = rawExcel?.upload?.isExcelBypass;

        if (rawExcel && !isExcelBypass && uploadedExcelFile) {
          const queryParams = new URLSearchParams(window.location.search);
          const quoteId = queryParams.get("quoteId") || "";
          const itineraryId = queryParams.get("itineraryId") || "";
          uploadTemplate(uploadedExcelFile, quoteId, itineraryId, false).catch(
            (err) => console.error("Excel upload failed:", err),
          );
        }

        const checkoutPayload =
          formData as unknown as TravellerDetailsV2CheckoutFormData;
        await onBuyProp?.(checkoutPayload);
      } catch (e) {
        console.error("Payment processing error:", e);
      } finally {
        setLocalSubmitting(false);
      }
    },
    [
      analytics,
      type,
      components,
      uploadedExcelFile,
      onBuyProp,
      getSelectedCountry,
      selectedCountry,
    ],
  );

  const submitForm = useCallback(() => {
    handleSubmit(onSubmit, onSubmitError)();
  }, [handleSubmit, onSubmit, onSubmitError]);

  return {
    localSubmitting,
    submitForm,
  };
}

